/* eslint-disable */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fmtDate } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const COLORS = {
  Reserved: "bg-amber-100 text-amber-800 border-amber-200",
  "Picked Up": "bg-blue-100 text-blue-800 border-blue-200",
  Returned: "bg-gray-100 text-gray-700 border-gray-200",
  Overdue: "bg-red-100 text-red-800 border-red-200",
  Cancelled: "bg-gray-100 text-gray-500 border-gray-200 line-through",
};

export default function ReservationsCalendar({ reservations }) {
  const nav = useNavigate();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const startDow = first.getDay();
    const days = [];
    // pad start with previous month
    for (let i = 0; i < startDow; i++) {
      const d = new Date(first);
      d.setDate(d.getDate() - (startDow - i));
      days.push({ date: d, current: false });
    }
    for (let i = 1; i <= last.getDate(); i++) {
      days.push({ date: new Date(cursor.getFullYear(), cursor.getMonth(), i), current: true });
    }
    // pad to multiple of 7
    while (days.length % 7 !== 0) {
      const d = new Date(days[days.length - 1].date);
      d.setDate(d.getDate() + 1);
      days.push({ date: d, current: false });
    }
    return days;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const r of reservations) {
      try {
        const p = new Date(r.pickup_datetime);
        const ret = new Date(r.return_datetime);
        const cur = new Date(p.getFullYear(), p.getMonth(), p.getDate());
        const end = new Date(ret.getFullYear(), ret.getMonth(), ret.getDate());
        while (cur <= end) {
          const k = cur.toISOString().slice(0, 10);
          if (!map[k]) map[k] = [];
          map[k].push(r);
          cur.setDate(cur.getDate() + 1);
        }
      } catch {
        // skip malformed dates
      }
    }
    return map;
  }, [reservations]);

  const monthLabel = cursor.toLocaleString("en-US", { month: "long", year: "numeric" });
  const isToday = (d) => d.toDateString() === today.toDateString();

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="reservations-calendar">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="font-heading text-lg font-semibold">{monthLabel}</h3>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="cal-prev">
            <ChevronLeft size={14} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} data-testid="cal-today">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="cal-next">
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="px-2 py-2 text-center font-semibold">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {grid.map((g, idx) => {
          const key = g.date.toISOString().slice(0, 10);
          const events = eventsByDay[key] || [];
          return (
            <div
              key={idx}
              className={`min-h-[88px] border-b border-r border-gray-100 p-1.5 ${g.current ? "bg-white" : "bg-gray-50/60"}`}
            >
              <div className={`text-xs font-medium mb-1 inline-flex items-center justify-center ${isToday(g.date) ? "bg-black text-white rounded-full w-6 h-6" : g.current ? "text-gray-700" : "text-gray-400"}`}>
                {g.date.getDate()}
              </div>
              <div className="space-y-0.5">
                {events.slice(0, 3).map((r) => (
                  <button
                    key={`${r.id}-${key}`}
                    onClick={() => nav(`/admin/reservations/${r.id}`)}
                    className={`block w-full text-[10px] px-1.5 py-0.5 rounded border text-left truncate hover:opacity-80 transition ${COLORS[r.status] || COLORS["Reserved"]}`}
                    title={`${r.customer_name} · ${r.vehicle_label} (${r.status})`}
                    data-testid={`cal-event-${r.id}-${key}`}
                  >
                    {r.customer_name?.split(" ")[0]} · {r.vehicle_label?.split(" ").slice(1, 3).join(" ")}
                  </button>
                ))}
                {events.length > 3 && (
                  <div className="text-[10px] text-gray-500 px-1.5">+{events.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
